import { useQuery } from '@tanstack/react-query'
import { ethers } from 'ethers'
import { getReadProvider } from './useProvider'
import { CHALLENGE_ABI } from '../abis/ChallengeABI'
import { getCurrentRound, type ChallengeDetail, type ProofType } from '../types/challenge'

const ZERO_BYTES32 = '0x' + '00'.repeat(32)

async function fetchChallengeDetailFn(
  address: string,
  userAddress: string | null,
): Promise<ChallengeDetail> {
  const provider = getReadProvider()
  const contract = new ethers.Contract(address, CHALLENGE_ABI, provider)

  const baseReads: Promise<unknown>[] = [
    contract.state() as Promise<bigint>,
    contract.creator() as Promise<string>,
    contract.challengeType() as Promise<bigint>,
    contract.startTime() as Promise<bigint>,
    contract.endTime() as Promise<bigint>,
    contract.entryFee() as Promise<bigint>,
    contract.winnerCount() as Promise<bigint>,
    contract.minParticipants() as Promise<bigint>,
    contract.maxParticipants() as Promise<bigint>,
    contract.participantCount() as Promise<bigint>,
    contract.getRoundDeadlines() as Promise<bigint[]>,
  ]
  if (userAddress) {
    baseReads.push(contract.isParticipant(userAddress) as Promise<boolean>)
    baseReads.push(contract.refunded(userAddress) as Promise<boolean>)
  }

  const results = await Promise.all(baseReads)
  const [
    state,
    creator,
    challengeType,
    startTime,
    endTime,
    entryFee,
    winnerCount,
    minParticipants,
    maxParticipants,
    participantCount,
    roundDeadlines,
  ] = results as [
    bigint,
    string,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint,
    bigint[],
  ]
  const isParticipant = userAddress ? Boolean(results[11]) : false
  const isRefunded = userAddress ? Boolean(results[12]) : false

  // Has the user already submitted a proof for the current round?
  let currentRoundDone = false
  const deadlines = (roundDeadlines as bigint[]).map(Number)
  if (userAddress && isParticipant && deadlines.length > 0) {
    const cr = getCurrentRound(deadlines)
    const eff = Math.min(cr, deadlines.length - 1)
    const h = await (contract.proofHash(userAddress, eff) as Promise<string>)
    currentRoundDone = h !== ZERO_BYTES32
  }

  // Display name + description come from off-chain storage (Cloudflare KV) — they are
  // pure metadata and don't affect money, so they're not read from the chain.
  let name = ''
  let description: string | null = null
  let proofType: ProofType = 'photo'
  const apiBase = import.meta.env.VITE_API_BASE_URL || ''
  try {
    const resp = await fetch(`${apiBase}/api/challenges/${address}`)
    if (resp.ok) {
      const data = (await resp.json()) as {
        title?: string
        description?: string
        proofType?: string
      }
      name = data.title ?? ''
      description = data.description ?? null
      if (data.proofType === 'checkin' || data.proofType === 'text' || data.proofType === 'photo') {
        proofType = data.proofType
      }
    }
  } catch {
    // Off-chain not available — not a hard error
  }
  if (!name) name = address

  return {
    address,
    name,
    creator: creator.toLowerCase(),
    challengeType: Number(challengeType),
    state: Number(state),
    startTime: Number(startTime),
    endTime: Number(endTime),
    entryFee: BigInt(entryFee.toString()),
    winnerCount: Number(winnerCount),
    participantCount: Number(participantCount),
    maxParticipants: Number(maxParticipants),
    minParticipants: Number(minParticipants),
    roundDeadlines: roundDeadlines.map(Number),
    isParticipant,
    isRefunded,
    currentRoundDone,
    description,
    proofType,
  }
}

export function useChallengeDetail(address: string, userAddress: string | null) {
  return useQuery<ChallengeDetail>({
    queryKey: ['challenge-detail', address, userAddress],
    queryFn: () => fetchChallengeDetailFn(address, userAddress),
    staleTime: 30_000,
    enabled: Boolean(address),
  })
}
